<template>
  <div v-if="currentConsumption !== undefined" id="content-page-table" class="table-fix-head" :style="fixHeadCalculatedStyle">
    <table class="table table-hover table-sm table-fit align-middle">
      <thead>
        <tr>
          <th>
            {{ $t('field.generic.item') }}
            <font-awesome-icon :icon="['fas', 'sort-up']" />
          </th>
          <th>{{ $t('field.generic.quantity') }}</th>
          <th>{{ $t('field.generic.status') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(entry, index) in uiList" :key="index">
          <td><ItemField :item="entry.item" :substitute-item-code="entry.foreignItem?.code" /></td>
          <td><QuantityField :quantity="entry.quantity" :unit-name="entry.item.baseUnitCode" :pad-length="maxQuantityLength" /></td>
          <td>
            <font-awesome-icon v-if="currentConsumption.nonBillable" :icon="['fas', 'dash']" fixed-width />
            <font-awesome-layers v-else-if="!entry.item.isBillable" fixed-width class="fa-lg" :title="$t('page.consumption.nonBillableDescription')">
              <font-awesome-icon :icon="['fas', 'slash']" :mask="['fas', 'coins']" class="text-muted" />
              <font-awesome-icon :icon="['fas', 'slash']" transform="up-1 right-1" class="text-danger" />
            </font-awesome-layers>
            <span v-else>
              <ConsumptionLineExportStatus :status="entry.exportStatus" :title="entry.exportResult"/>
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
  <div v-else>
    <slot name="empty-list" />
  </div>

</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useItemsStore, useConsumptionsStore, useForeignItemsStore } from '@/stores';
import { ItemField, QuantityField } from "@/components/fields";
import ConsumptionLine from '@/models/frontend/ConsumptionLine';
import ConsumptionLineExportStatus from '@/components/consumptions/ConsumptionLineExportStatus.vue';

const consumptionsStore = useConsumptionsStore();
const itemsStore = useItemsStore();
const foreignItemsStore = useForeignItemsStore();

const props = defineProps({
  consumptionId: { type: Number, required: true }
});

const offsetTop = ref(0);
const offsetTopInterval = ref<NodeJS.Timeout | null>(null);

onMounted(() => {
  offsetTopInterval.value = setInterval(() => {
    offsetTop.value = document.querySelector('#content-page-table')?.getBoundingClientRect().top ?? 0;
    if (offsetTop.value !== 0) {
      stopTableOffsetRecalculation();
    }
  }, 1000);

  offsetTop.value = document.querySelector('#content-page-table')?.getBoundingClientRect().top ?? 0;
});

onUnmounted(() => {
  stopTableOffsetRecalculation();
});

function stopTableOffsetRecalculation() {
  if (offsetTopInterval.value) {
    clearInterval(offsetTopInterval.value);
    offsetTopInterval.value = null;
  }
}

const fixHeadCalculatedStyle = computed(() => {
  return {
    height: `calc(100vh - ${offsetTop.value}px)`,
  };
});

const uiList = computed(() => {
  if (currentConsumption.value === undefined) {
    return [];
  }
  const mappedLines = currentConsumption.value.lines.map((line) => {
    const item = itemsStore.getById(line.itemId);
    const foreignItem = foreignItemsStore.getById(line.itemId);
    return new ConsumptionLine(item, item.baseUnitCode, line.quantity, line.exportStatus, line.exportReference, line.exportResult, line.lot, line.bestBefore, line.expiry, false, line.trackStock, foreignItem);
  });
  return [...mappedLines].sort((a, b) =>
    a.item.name.localeCompare(b.item.name)
  );
});

const maxQuantityLength = computed(() => {
  return uiList.value.reduce((acc, item) => Math.max(acc, item.quantity.toString().length), 0);
});

const currentConsumption = computed(() => {
  return consumptionsStore.getById(props.consumptionId)
});
</script>
